import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Input,
  Badge,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BreadCrumb from '../../../../Components/Common/BreadCrumb';
import ActionButton from '../../../../Components/Common/ActionButton';
import * as api from './invitationExternalApi';
import {
  INVITATION_STATUS_BADGE,
  DEFAULT_PAGE_SIZE,
} from './invitationExternalConstants';

// Invitation - External (DMUT) — List.
//
// The logged-in DMUT's invitations. Read-only: the single action is View Detail,
// which opens the same Detail page for all four statuses (Open / Accepted /
// Declined / Expired) — the status only decides whether Accept / Decline appear
// there, not which page is shown.
//
// Search covers Title, Id Procurement and Description only (DOD item 2); there is
// no Advance Search (item 3). Pagination and sorting are delegated to the API
// layer so the dummy store and a future live endpoint behave identically.

const headerLabel = label => (
  <span className='form-label text-uppercase fw-semibold fs-13 mb-0'>
    {label}
  </span>
);

const InvitationExternalListView = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  document.title = 'Invitation - External';

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sortField, sortDirection]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { items: rows, totalCount } = await api.listInvitations({
        page: currentPage,
        pageSize: rowsPerPage,
        searchText: debouncedSearch,
        sortField,
        sortOrder: sortDirection,
      });
      setItems(rows);
      setTotalRows(totalCount);
    } catch (error) {
      toast.error(`Failed to load invitations: ${error}`);
      setItems([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, currentPage, rowsPerPage, sortField, sortDirection]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (column, direction) => {
    setSortField(column.sortKey || null);
    setSortDirection(direction);
  };

  // Each column declares a minWidth sized to its content — without one, RDT
  // divides the container evenly and every column ends up cramped.
  const columns = [
    {
      name: headerLabel('Id Procurement'),
      sortKey: 'procId',
      sortable: true,
      minWidth: '200px',
      selector: row => row.procId,
      cell: row => <span className='fw-semibold'>{row.procId}</span>,
    },
    {
      name: headerLabel('Title'),
      sortKey: 'title',
      sortable: true,
      wrap: true,
      minWidth: '280px',
      selector: row => row.title,
    },
    {
      name: headerLabel('Procurement Type'),
      sortKey: 'procurementType',
      sortable: true,
      wrap: true,
      minWidth: '210px',
      selector: row => row.procurementType,
    },
    {
      name: headerLabel('Procurement Method'),
      sortKey: 'procurementMethod',
      sortable: true,
      wrap: true,
      minWidth: '210px',
      selector: row => row.procurementMethod,
    },
    {
      name: headerLabel('Description'),
      sortKey: 'description',
      sortable: true,
      wrap: true,
      minWidth: '320px',
      selector: row => row.description,
    },
    {
      // The spec sheet labels this "Invitation Internal Status" while the module
      // itself is the External one — read as a copy/paste slip from the Internal
      // counterpart. Labelled by what it actually holds: this DMUT's own
      // response state (Open / Accepted / Declined / Expired).
      name: headerLabel('Invitation Status'),
      sortKey: 'invitationStatus',
      sortable: true,
      center: true,
      minWidth: '170px',
      selector: row => row.invitationStatus,
      cell: row => (
        <Badge
          color={INVITATION_STATUS_BADGE[row.invitationStatus] || 'secondary'}
          className='px-2'
        >
          {row.invitationStatus}
        </Badge>
      ),
    },
    {
      name: headerLabel('Action'),
      minWidth: '100px',
      center: true,
      // Not sortable (DOD item 6: every column except Action).
      cell: row => (
        <ActionButton
          variant='icon'
          size='sm'
          icon='ri-eye-line'
          label='View Detail'
          onClick={() => navigate(`/crude/invitation-external/dtl/${row.id}`)}
        />
      ),
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb
            title='Invitation - External'
            pageTitle='Plan & Procurement'
          />

          <Row>
            <Col lg={12}>
              <Card id='InvitationExternalList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <h5 className='card-title mb-0 flex-grow-1'>
                      Invitation List (Registered Supplier)
                    </h5>
                    <div className='flex-shrink-0'>
                      <div className='search-box'>
                        <Input
                          type='text'
                          className='form-control form-control-sm'
                          placeholder='Search Title / Id / Description...'
                          value={searchInput}
                          onChange={e => setSearchInput(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div className='table-responsive'>
                    <DataTable
                      columns={columns}
                      data={items}
                      highlightOnHover
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationPerPage={rowsPerPage}
                      paginationDefaultPage={currentPage}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      onChangePage={page => setCurrentPage(page)}
                      onChangeRowsPerPage={newPerPage => {
                        setRowsPerPage(newPerPage);
                        setCurrentPage(1);
                      }}
                      sortServer
                      onSort={handleSort}
                      wrap
                      dense
                      striped
                      progressPending={loading}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-mail-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          No invitations found
                        </div>
                      }
                      customStyles={{
                        headCells: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid #dee2e6',
                            whiteSpace: 'nowrap',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: { style: { minHeight: '50px' } },
                      }}
                    />
                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default InvitationExternalListView;
